import { ThemeProvider } from "@emotion/react";
import { Box } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { LoadScript } from "@react-google-maps/api";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter as Router } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./App.css";
import EmailVerifyWarning from "./components/commons/EmailVerifyWarning";
import Contents from "./components/Contents";
import EmailBouncedModal from "./components/EmailBouncedModal";
import Footer from "./components/Footer";
import Loader from "./components/Loader";
import Navbar from "./components/Navbar";
import UserAvatarModal from "./components/UserAvatarModal";
import usePreferredTheme from "./hooks/usePreferredTheme";
import { checkAuth } from "./redux/authSlice";
import { getCookie, setCookie } from "./utils/cookies";

const googleMapAPIKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

const getGoogleMapsLocaleParams = (locale) => {
  const normalized = String(locale || "en").replace(/_/g, "-");
  const [languagePart = "en", regionPart] = normalized.split("-");

  const language = languagePart.toLowerCase();
  const region = /^[A-Za-z]{2}$/.test(regionPart || "")
    ? regionPart.toUpperCase()
    : undefined;

  return { language, region };
};

function App() {
  const { t, i18n } = useTranslation();
  const [mapsLocale] = useState(() =>
    getGoogleMapsLocaleParams(
      i18n.resolvedLanguage || i18n.language || "en"
    )
  );
  useEffect(() => {
    const cookieName = "firstVisitUrl";
    const existingUrl = getCookie(cookieName);

    if (!existingUrl) {
      const currentUrl = window.location.href;
      setCookie(cookieName, currentUrl, 365);
    }
  }, []);
  // check auth when app first loads
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(checkAuth());
  }, [dispatch]);
  const deviceTheme = usePreferredTheme();
  const [mode, setMode] = useState(deviceTheme);
  useEffect(() => {
    setMode(deviceTheme);
    if (deviceTheme === "dark") document.documentElement.classList.add("dark");
    if (deviceTheme === "light")
      document.documentElement.classList.remove("dark");
  }, [deviceTheme]);

  // Register the service worker for Browser Push Notifications
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((registration) => {
          console.log("Service Worker registered with scope:", registration.scope);
          // registration.pushManager
          //   .getSubscription({
          //     userVisibleOnly: true,
          //     applicationServerKey: process.env.REACT_APP_VAPID_PUBLIC_KEY,
          //   })
          //   .then((subscription) => {
          //     if (subscription) {
          //       console.log("Already subscribed to push notifications:", subscription);
          //     } else {
          //       console.log("Not subscribed to push notifications.");
          //     }
          //   })
          //   .catch((error) => {
          //     console.error("Error getting push subscription:", error);
          //   });
        })
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });
    }
  }, []);
  const { profile, isLoggedIn } = useSelector((state) => state.auth);

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: '"Manrope", "Helvetica", "Arial", sans-serif',
          button: {
            textTransform: "unset",
          },
        },
        palette: {
          mode,
          background: {
            paper: mode === "light" ? "#f0f2f5" : "#242526",
          },
        },
        components: {
          MuiAvatar: {
            defaultProps: {
              variant: "square",
            },
            styleOverrides: {
              root: {
                borderRadius: 7,
              },
            },
          },
        },
      }),
    [mode]
  );

  const [emailBouncedModalShown, setEmailBouncedModalShown] = useState(true);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          flexFlow: "column",
          minHeight: "100dvh",
          bgcolor: theme.palette.background.default,
        }}
      >
        <LoadScript
          googleMapsApiKey={googleMapAPIKey}
          language={mapsLocale.language}
          region={mapsLocale.region}
          loadingElement={<Loader />}
        >
          <Router>
            <Suspense fallback={<Loader />}>
              {isLoggedIn && profile.email && !profile.emailVerified && (
                <EmailVerifyWarning userEmail={profile.email} />
              )}
            </Suspense>
            <Navbar mode={mode} setMode={setMode} />
            <Suspense fallback={<Loader />}>
              <Contents />
            </Suspense>
            {!isLoggedIn && <Footer mode={mode} setMode={setMode} />}
            {profile && profile?.sendNoEmails === true && (
              <EmailBouncedModal
                showModal={emailBouncedModalShown}
                handleCloseModal={() => setEmailBouncedModalShown(false)}
                t={t}
              />
            )}
          </Router>
          <ToastContainer position="bottom-left" theme={mode} />
          <UserAvatarModal />
        </LoadScript>
      </Box>
    </ThemeProvider>
  );
}

export default App;
